import React, { useState, useEffect, useCallback } from "react";
import type { GameName, Completion, PlayerStats } from "../types.ts";
import { ALL_GAMES } from "../types.ts";

const GAME_META: Record<GameName, { emoji: string; color: string; label: string }> = {
  foldaway: { emoji: "\uD83D\uDCD0", color: "#A78BFA", label: "Foldaway" },
  spillover: { emoji: "\uD83D\uDCA7", color: "#4ECAFF", label: "Spillover" },
  lockstep: { emoji: "\u2699\uFE0F", color: "#FFD166", label: "Lockstep" },
  burnout: { emoji: "\uD83D\uDD25", color: "#FF6B6B", label: "Burnout" },
  echo: { emoji: "\uD83D\uDCE1", color: "#00F0A0", label: "Echo" },
};

export function Dashboard({
  token,
  displayName,
  onLogout,
}: {
  token: string;
  displayName: string;
  onLogout: () => void;
}) {
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [today, setToday] = useState<Completion[]>([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, todayRes] = await Promise.all([
        fetch("/api/stats", { headers }),
        fetch("/api/completions/today", { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (todayRes.ok) setToday(await todayRes.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const todayGames = new Set(today.map((c) => c.game));
  const todayStars = today.reduce((s, c) => s + c.stars, 0);

  return (
    <div className="pt-[80px] pb-16">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono text-[10px] tracking-[5px] uppercase text-electric-mint-dim mb-2">
            Dashboard
          </div>
          <h2 className="font-display font-extrabold text-3xl tracking-[-1px]">
            Welcome back, {displayName}
          </h2>
        </div>
        <button
          onClick={onLogout}
          className="font-display text-xs px-4 py-2 rounded-[10px] border border-[rgba(255,107,107,0.25)] text-neon-coral hover:bg-[rgba(255,107,107,0.1)] transition-colors"
        >
          Log out
        </button>
      </div>

      {loading ? (
        <p className="text-warm-gray text-sm">Loading...</p>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            <StatCard value={String(stats?.streak ?? 0)} label="Day Streak" />
            <StatCard value={String(stats?.totalStars ?? 0)} label="Total Stars" color="#FFD166" />
            <StatCard value={String(stats?.totalPlayed ?? 0)} label="Games Played" color="#4ECAFF" />
            <StatCard value={String(stats?.maxStreak ?? 0)} label="Best Streak" color="#A78BFA" />
          </div>

          {/* Daily Five Progress */}
          <div className="mb-10">
            <h3 className="font-display font-bold text-lg mb-4">Today&apos;s Daily Five</h3>
            <div className="grid grid-cols-5 gap-3">
              {ALL_GAMES.map((game) => {
                const meta = GAME_META[game];
                const completion = today.find((c) => c.game === game);
                const done = !!completion;
                return (
                  <div
                    key={game}
                    className={`rounded-[12px] p-4 text-center border transition-all ${
                      done
                        ? "border-opacity-40"
                        : "border-[rgba(255,255,255,0.06)] opacity-40"
                    }`}
                    style={{
                      background: done
                        ? `linear-gradient(145deg, #141729, ${meta.color}20)`
                        : "#141729",
                      borderColor: done ? meta.color : undefined,
                    }}
                  >
                    <div className="text-xl mb-1">{meta.emoji}</div>
                    <div className="font-display font-semibold text-xs mb-1" style={{ color: done ? meta.color : "#5A5750" }}>
                      {meta.label}
                    </div>
                    {done ? (
                      <div className="text-amber-spark text-xs">
                        {"\u2605".repeat(completion!.stars)}{"\u2606".repeat(3 - completion!.stars)}
                      </div>
                    ) : (
                      <div className="text-[10px] text-mid-gray">Not played</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-center">
              <span className="font-mono text-sm text-warm-gray">
                {todayGames.size}/5 games &middot; {todayStars}/15 stars
              </span>
              {todayGames.size === 5 && (
                <span className="ml-3 font-display font-bold text-electric-mint text-sm">
                  Daily Five Complete!
                </span>
              )}
            </div>
          </div>

          {/* Per-game breakdown */}
          <div>
            <h3 className="font-display font-bold text-lg mb-4">Game Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              {ALL_GAMES.map((game) => {
                const meta = GAME_META[game];
                const bd = stats?.gameBreakdown[game];
                return (
                  <div
                    key={game}
                    className="rounded-[12px] p-4 border border-[rgba(255,255,255,0.06)]"
                    style={{ background: `linear-gradient(145deg, #141729, ${meta.color}10)` }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span>{meta.emoji}</span>
                      <span className="font-display font-semibold text-xs" style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="font-mono text-xs text-warm-gray space-y-1">
                      <div>Played: <span className="text-chalk">{bd?.played ?? 0}</span></div>
                      <div>Stars: <span className="text-amber-spark">{bd?.stars ?? 0}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <div className="rounded-[12px] bg-deep-navy border border-dark-gray p-5">
      <div className="font-display font-extrabold text-2xl mb-1" style={{ color: color ?? "#F2F0ED" }}>
        {value}
      </div>
      <div className="font-mono text-[10px] tracking-[1px] uppercase text-warm-gray">{label}</div>
    </div>
  );
}
